<template>
<ProgressCircular v-if="loading" />
  <form v-else novalidate ref="updateRoleForm" :disabled="loading" @submit.prevent="() => {}">
    <v-row no-gutters>
      <v-col cols="8">
        <v-card max-width="1200" flat color="transparent">
          <v-card-title class="mb-10">
            <FormHeader
              :loading="loading"
              :disabled="disabled"
              :link="'page-roles'"
              :title="getTitle ? `${getTitle} роль` : `Создать роль`"
            />
          </v-card-title>
          <v-tabs v-model="activeTab">
            <v-tab v-for="tab in tabs" :key="tab.value" :disabled="tab.isDisabled || disabled">
              {{ tab.title }}
            </v-tab>
          </v-tabs>
          <v-tabs-items v-model="activeTab">
            <v-tab-item>
              <v-card-text>
                <PageContent height="80">
                  <v-container>
                    <v-row>
                      <v-col cols="12">
                        <span>Имя *</span>
                        <v-text-field
                          outlined
                          autofocus
                          id="name"
                          :error-messages="validationErrors.name"
                          :disabled="disabled"
                          :loading="loading"
                          v-model="fields.name"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12">
                        <span>Key *</span>
                        <v-text-field
                          outlined
                          autofocus
                          id="key"
                          :error-messages="validationErrors.key"
                          :disabled="disabled"
                          :loading="loading"
                          v-model="fields.key"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12">
                        <span>Описание</span>
                        <v-textarea
                          outlined
                          :disabled="disabled"
                          :loading="loading"
                          v-model="fields.description"
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </PageContent>
              </v-card-text>
            </v-tab-item>
            <v-tab-item>
              <v-card-text>
                <PageContent>
                  <v-list>
                    <v-list-group
                      v-for="permissionGroup in permissionGroups"
                      :key="permissionGroup.id"
                      no-action
                    >
                      <template v-slot:activator>
                        <v-list-item-content>
                          <v-list-item-title>
                            {{ permissionGroup.name }}
                            ({{ chosenPermissionAmount(permissionGroup.permissions) }})
                          </v-list-item-title>
                        </v-list-item-content>
                      </template>

                      <v-list-item
                        dense
                        :disabled="data.isProtected || disabled"
                        @click="updateGroupPermissionHandler(permissionGroup.permissions)"
                      >
                        <v-checkbox
                          hide-details
                          dense
                          :loading="loading"
                          :disabled="disabled || data.isProtected"
                          :input-value="
                          isAllPermissionsGroupChosen(permissionGroup, permissionsIdList)
                        "
                          :indeterminate="
                          !isAllPermissionsGroupChosen(
                            permissionGroup,
                            permissionsIdList
                          ) &&
                          permissionGroup.permissions.some(p =>
                            permissionsIdList.includes(p.id)
                          )
                        "
                          @click="updateGroupPermissionHandler(permissionGroup.permissions)"
                          @click.native.stop
                        />
                        <span> Все </span>
                      </v-list-item>
                      <v-divider />
                      <v-list-item
                        dense
                        v-for="permission in permissionGroup.permissions"
                        :key="permission.id"
                        :loading="loading"
                        :disabled="disabled || data.isProtected"
                        @click="
                        updatePermissionHandler(
                          permission.id,
                          permissionsIdList.includes(permission.id)
                        )
                      "
                      >
                        <v-list-item-content>
                          <v-list-item-title>
                            <v-row no-gutters align="center">
                              <v-checkbox
                                hide-details
                                dense
                                :loading="loading"
                                :disabled="disabled || data.isProtected"
                                :input-value="permissionsIdList.includes(permission.id)"
                                @change="
                                updatePermissionHandler(
                                  permission.id,
                                  permissionsIdList.includes(permission.id)
                                )
                              "
                                @click.native.stop
                              />
                              <span>
                              {{ permission.name }}
                            </span>
                            </v-row>
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list-group>
                  </v-list>
                </PageContent>
              </v-card-text>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </v-col>
      <v-col cols="4">
        <ManageSidePanel
          ref="manageSidePanel"
          :is-new="!data.id"
          :created-at="data.createdAt"
          :updated-at="data.updatedAt"
          :published-at="data.publishedAt"
          :author="data.author ? data.author.name : ''"
          :is-loading="loading"
          :is-protected="data.isProtected"
          :is-submit-disabled="
            activeTab === tabs.findIndex(tab => tab.value === 'permissions') || disabled
          "
          :entity-id="data.id"
          :entity-type="'role'"
          @delete="$emit('delete-role')"
          @cancel="$router.push({ name: 'page-roles' })"
          @submit="submit"
        />
      </v-col>
    </v-row>
  </form>
</template>
<script>
import FormAbstract from '@/components/abstract/form'
import validationRules from './validation-rules'
import ManageSidePanel from '@/components/manage-side-panel'
import permissionGroupsQuery from '@/services/gql/types/PermissionGroups/queries/permissionGroups.gql'
import FormHeader from '@/components/form-header'
import PageContent from '@/components/page-content/PageContent.vue'
import ProgressCircular from '@/components/progress-circular'

export default {
  name: 'FormRole',
  validationRules,
  extends: FormAbstract,
  components: {
    PageContent,
    ManageSidePanel,
    FormHeader,
    ProgressCircular
  },
  props: {
    data: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    tabs () {
      return [
        {
          title: 'Основное',
          value: 'main'
        },
        {
          title: 'Разрешения',
          value: 'permissions',
          isDisabled: !this.data.id
        }
      ]
    },
    permissionsIdList () {
      if (this.data.permissions === undefined) {
        return []
      }
      return this.data.permissions.map(p => p.id)
    },
    getTitle () {
      if (this.data) {
        return this.data?.name || ''
      }
      return null
    }
  },
  data () {
    return {
      permissionGroups: [],
      activeTab: 0,
      fields: {
        name: null,
        key: null,
        description: null,
        isProtected: false
      }
    }
  },
  watch: {
    data (data) {
      this.populate(data)
    }
  },
  methods: {
    isAllPermissionsGroupChosen (permissionGroup, permissionsIdList) {
      return permissionGroup.permissions.every(p => permissionsIdList.includes(p.id))
    },
    updatePermissionHandler (value, isActive) {
      const permissions = {}
      const item = { id: value }
      isActive ? (permissions.disconnect = item) : (permissions.connect = item)
      this.$emit('submit', { permissions })
    },
    updateGroupPermissionHandler (value) {
      let permissions = {}
      const item = []
      const permissionList = value.map(p => p.id)
      permissionList.forEach(id => {
        if (!this.permissionsIdList.includes(id)) item.push({ id })
      })
      // isDisconnect
      if (permissionList.some(p => !this.permissionsIdList.includes(p))) {
        permissions = {}
        permissions.disconnect = item
      }
      // isConnect
      if (permissionList.some(p => this.permissionsIdList.includes(p))) {
        permissions = {}
        permissions.connect = item
      }
      // isAllConnect
      if (permissionList.every(p => this.permissionsIdList.includes(p))) {
        permissions = {}
        permissions.disconnect = item
      }
      // isAllDisconnect
      if (permissionList.every(p => !this.permissionsIdList.includes(p))) {
        permissions = {}
        permissions.connect = item
      }
      this.$emit('submit', { permissions })
    },
    chosenPermissionAmount (permissions) {
      const chosenPermissions = []
      permissions.forEach(p => {
        if (this.permissionsIdList.includes(p.id)) chosenPermissions.push(p.id)
      })
      return `${chosenPermissions.length}/${permissions.map(p => p.id).length}`
    },
    async submit () {
      const isValid = await this.validate()
      if (!isValid) {
        this.mixScrollToError(this.$v)
        return
      }
      const payload = {
        ...this.composeFormData(this.fields)
      }
      this.$emit('submit', payload)
    }
  },
  apollo: {
    permissionGroups: {
      fetchPolicy: 'network-only',
      query: permissionGroupsQuery,
      manual: true,
      variables: {
        where: {}
      },
      result (res) {
        this.permissionGroups = res.data.permissionGroups.list
      }
    }
  }
}
</script>
